import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { useDialogPluginComponent } from "quasar"
import SfxBtn from "src/components/button/SfxBtn.vue"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxIssueAdditionalRequirements from "src/components/issue/SfxIssueAdditionalRequirements.vue"
import SfxIssueCorrectiveAction from "src/components/issue/SfxIssueCorrectiveAction.vue"
import SfxIssueTimeRequirements from "src/components/issue/SfxIssueTimeRequirements.vue"
import { getDB } from "src/db/databases"
import { Log } from "src/util/classes/log"
import { showErrorNotification, showSuccessNotification } from "src/util/functions/notifications"
import { patchIssuesOnItemAction, postIssuesOnItemAction } from "src/util/http/csi/issues"
import { PayloadEntity } from "src/util/types/dataModels/genericObject"
import { IssuesOnItemAction } from "src/util/types/dataModels/issue"
import { onMounted, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxDialogEditCorrectionInfo',
  props: {
  issueId: {
    type: String,
    required: true,
  },

  issueOnItemAction: {
    type: Object as PropType<IssuesOnItemAction>,
  },
},
  emits: [...useDialogPluginComponent.emits],
  setup(__props) {

const props = __props



//endregion

//region Variables

const { t } = useI18n()
const { dialogRef, onDialogCancel, onDialogOK } = useDialogPluginComponent()

const title = t("sfxDialogEditCorrectionInfo.title")
const btnCancel = t("sfxButtons.btn_cancel")
const btnSave = t("sfxButtons.btn_save")

const correctiveAction = ref<string | undefined>()
const timeEstimate = ref<string | undefined>()
const requiresIsolation = ref<boolean | undefined>()
const requiresShutdown = ref<boolean | undefined>()

const correctiveActionEl = ref<typeof SfxIssueCorrectiveAction | undefined>()
const correctiveTimeRequirementsEl = ref<typeof SfxIssueTimeRequirements | undefined>()
const correctiveAdditionalRequirementsEl = ref<typeof SfxIssueAdditionalRequirements | undefined>()

//endregion

//region Lifecycle

onMounted(() => {
  correctiveAction.value = props.issueOnItemAction?.text
  timeEstimate.value = props.issueOnItemAction?.time_estimate
  requiresIsolation.value = props.issueOnItemAction?.isolation_required
  requiresShutdown.value = props.issueOnItemAction?.shutdown_required
})

//endregion

//region Functions

async function submitData() {
  try {
    if (!correctiveAction.value) return

    const action: PayloadEntity<IssuesOnItemAction> = {
      text: correctiveAction.value,
      time_estimate: timeEstimate.value,
      isolation_required: requiresIsolation.value,
      shutdown_required: requiresShutdown.value,
      issue: props.issueId,
    }

    const issueAction = await (props.issueOnItemAction
      ? patchIssuesOnItemAction({}, props.issueOnItemAction?.id, action)
      : postIssuesOnItemAction({}, action))

    if (issueAction) {
      const db = await getDB()
      await db.issue_on_item_actions.upsert(issueAction)

      onDialogOK(issueAction)
      showSuccessNotification(t("sfxDialogEditCorrectionInfo.issue_actions_updated"))
    }
  } catch (error) {
    showErrorNotification(t("sfxErrorMsg.error_updating_corrective_action"))
    Log.error({ message: "An error occurred while updating corrective action(s)", error })
  }
}

//endregion

//region Setup Code

watch(
  () => correctiveActionEl.value,
  () => {
    if (correctiveAction.value) correctiveActionEl.value?.forceShow()

    if (timeEstimate.value && timeEstimate.value !== "00:00:00") {
      correctiveTimeRequirementsEl.value?.forceShow()
    }

    if (requiresIsolation.value || requiresShutdown.value) {
      correctiveAdditionalRequirementsEl.value?.forceShow()
    }
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_card_section = _resolveComponent("q-card-section")!
  const _component_q_card_actions = _resolveComponent("q-card-actions")!
  const _component_q_form = _resolveComponent("q-form")!
  const _component_q_card = _resolveComponent("q-card")!
  const _component_q_dialog = _resolveComponent("q-dialog")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_card, { class: "q-dialog-plugin" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_card_section, { class: "text-h3" }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(title)), 1)
            ]),
            _: 1
          }),
          _createVNode(_component_q_form, { onSubmit: submitData }, {
            default: _withCtx(() => [
              _createVNode(_component_q_card_section, { class: "q-py-none" }, {
                default: _withCtx(() => [
                  _createVNode(SfxIssueCorrectiveAction, {
                    ref_key: "correctiveActionEl",
                    ref: correctiveActionEl,
                    modelValue: correctiveAction.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((correctiveAction).value = $event)),
                    required: ""
                  }, null, 8, ["modelValue"]),
                  _createVNode(SfxIssueTimeRequirements, {
                    ref_key: "correctiveTimeRequirementsEl",
                    ref: correctiveTimeRequirementsEl,
                    "time-estimate": timeEstimate.value,
                    "onUpdate:timeEstimate": _cache[1] || (_cache[1] = ($event: any) => ((timeEstimate).value = $event))
                  }, null, 8, ["time-estimate"]),
                  _createVNode(SfxIssueAdditionalRequirements, {
                    ref_key: "correctiveAdditionalRequirementsEl",
                    ref: correctiveAdditionalRequirementsEl,
                    "isolation-checked": requiresIsolation.value,
                    "onUpdate:isolationChecked": _cache[2] || (_cache[2] = ($event: any) => ((requiresIsolation).value = $event)),
                    "shutdown-checked": requiresShutdown.value,
                    "onUpdate:shutdownChecked": _cache[3] || (_cache[3] = ($event: any) => ((requiresShutdown).value = $event))
                  }, null, 8, ["isolation-checked", "shutdown-checked"])
                ]),
                _: 1
              }),
              _createVNode(_component_q_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(SfxBtnOutline, {
                    onClick: _unref(onDialogCancel),
                    label: _unref(btnCancel),
                    class: "col"
                  }, null, 8, ["onClick", "label"]),
                  _createVNode(SfxBtn, {
                    type: "submit",
                    label: _unref(btnSave),
                    disable: !correctiveAction.value,
                    class: "col"
                  }, null, 8, ["label", "disable"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 512))
}
}

})